import { REGED_LEAD_TIME_OVERRIDE_REASON_MAX_LENGTH, RegEdComplianceErrorCode } from '@Queries/RegEdCompliance';
import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../components-v2/Button';
import { Dialog, DialogFooter, DialogHeader, DialogPopup, DialogTitle } from '../../../components-v2/Dialog';
import { TextArea } from '../../../components-v2/TextArea';
import { styleSheet } from './styles';

interface IProps {
	/** The rejection being resolved; absent closes the dialog. */
	rejection?: { errorCode: RegEdComplianceErrorCode; message: string } | null;
	isSubmitting?: boolean;
	onCancel(): void;
	onSubmit(reason: string): void;
}

/**
 * Collects the advisor's justification for requesting a compliance review inside the firm's notice
 * window. Reached by rejection rather than up front: the window and the question it applies to are
 * broker-dealer config the FE can't see, so the server is what knows the date is too soon.
 */
export const RegEdLeadTimeOverrideDialog: React.FC<IProps> = ({ rejection, isSubmitting, onCancel, onSubmit }) => {
	const [reason, setReason] = React.useState('');

	// A too-long reason comes back as a fresh rejection; keep what they typed so they can trim it.
	const isTooLong = rejection?.errorCode === RegEdComplianceErrorCode.ReviewLeadTimeOverrideReasonTooLong;
	React.useEffect(() => {
		if (rejection && !isTooLong) {
			setReason('');
		}
	}, [rejection, isTooLong]);

	const trimmed = reason.trim();
	const overLimit = trimmed.length > REGED_LEAD_TIME_OVERRIDE_REASON_MAX_LENGTH;

	return (
		<Dialog open={!!rejection} onOpenChange={open => (open ? undefined : onCancel())}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Request review on short notice</DialogTitle>
				</DialogHeader>
				<div className={css(styleSheet.body)}>
					<p className={css(styleSheet.message)}>{rejection?.message}</p>
					<label className={css(styleSheet.label)} htmlFor='reged-lead-time-reason'>
						Reason for the short notice
					</label>
					<TextArea
						id='reged-lead-time-reason'
						value={reason}
						onChange={event => setReason(event.target.value)}
						placeholder='Your compliance reviewer will read this.'
						disabled={isSubmitting}
						rows={4}
					/>
					<div className={css(styleSheet.counter, overLimit ? styleSheet.counterOverLimit : null)}>
						{trimmed.length} / {REGED_LEAD_TIME_OVERRIDE_REASON_MAX_LENGTH}
					</div>
				</div>
				<DialogFooter>
					<Button variant='secondary' onClick={onCancel} disabled={isSubmitting}>
						Cancel
					</Button>
					<Button
						onClick={() => onSubmit(trimmed)}
						disabled={!trimmed || overLimit || isSubmitting}
						isLoading={isSubmitting}
					>
						Submit for review
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
